import type { BankDispensaryMetadataData } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DispensariesApi } from 'api';
import type { ApiError } from 'api/api-util/api-error';
import { DISPENSARY_METADATA_KEY, MINIFIED_DISPENSARIES_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';
import { lookupAccountNumberError } from 'util/error-lookup.util';

export function useUpdateDispensaryMetadata(dispensaryId: string) {
  const { userBankId } = useUser();
  const dispensaryApi = new DispensariesApi();
  const queryClient = useQueryClient();
  const { mutateAsync: updateDispensaryMetadata, isPending: isPatchDispensaryMetadataPending } = useMutation({
    mutationKey: [DISPENSARY_METADATA_KEY],
    mutationFn: async (value: BankDispensaryMetadataData) => {
      try {
        return await dispensaryApi.saveMetaData(userBankId, dispensaryId, value);
      } catch (error) {
        throw new Error(lookupAccountNumberError(error as ApiError));
      }
    },
    onSuccess: () => {
      // Re-hydrate the accounts list too: it reads internal_metadata.accounts for ID search + filters.
      queryClient.invalidateQueries({ queryKey: [DISPENSARY_METADATA_KEY] });
      queryClient.invalidateQueries({ queryKey: [MINIFIED_DISPENSARIES_KEY, userBankId] });
    }
  });
  return { updateDispensaryMetadata, isPatchDispensaryMetadataPending };
}
